export const MCAP_VIRTUAL_PATH_PREFIX = '/__vll_mcap__/v1';

/**
 * The immutable identity of a byte source. Lease transport state deliberately
 * lives elsewhere: changing a signed URL must not change this value.
 */
export interface McapVirtualSourceIdentity {
  object: string;
  generation: string;
  blake3: string;
  sizeBytes: string;
}

/** Current transport state for an immutable source. */
export interface McapVirtualLease {
  url: string;
  expiresAt: string;
  authorization?: string;
}

/**
 * A page-scoped capability the Service Worker may use to answer virtual MCAP
 * requests. The worker keeps this object in memory only.
 */
export interface McapVirtualGrant {
  sessionNonce: string;
  sourceId: string;
  identity: McapVirtualSourceIdentity;
  lease: McapVirtualLease;
}

export type McapVirtualWorkerMessage =
  | { type: 'vll-mcap-register'; grant: McapVirtualGrant }
  | { type: 'vll-mcap-release'; sessionNonce: string; sourceId: string };

/** Reply sent through a MessageChannel after the worker has applied a grant mutation. */
export interface McapVirtualWorkerAcknowledgement {
  type: 'vll-mcap-ack';
  accepted: boolean;
}

export interface McapVirtualLeaseRefreshNeeded {
  type: 'vll-mcap-lease-refresh-needed';
  sessionNonce: string;
  sourceId: string;
}

let pageSessionNonce: string | undefined;

/**
 * A random 128-bit value kept only in this page's JavaScript realm. It is not
 * persisted, so a reload gets a distinct grant namespace.
 */
function createMcapVirtualSessionNonce(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** Returns the one random namespace shared by all virtual MCAP sources in this page. */
export function getMcapVirtualSessionNonce(): string {
  pageSessionNonce ??= createMcapVirtualSessionNonce();
  return pageSessionNonce;
}

/**
 * SHA-256 over four UTF-8 fields, each prefixed by its unsigned 32-bit byte
 * length. Fixed field order and byte lengths make this unambiguous even when a
 * source field contains separators.
 */
export async function createMcapVirtualSourceId(
  identity: McapVirtualSourceIdentity,
): Promise<string> {
  const fields = [identity.object, identity.generation, identity.blake3, identity.sizeBytes];
  const encoder = new TextEncoder();
  const encodedFields = fields.map((field) => encoder.encode(field));
  const byteLength = encodedFields.reduce((total, field) => total + 4 + field.byteLength, 0);
  const input = new Uint8Array(byteLength);
  const view = new DataView(input.buffer);
  let offset = 0;

  for (const field of encodedFields) {
    view.setUint32(offset, field.byteLength, false);
    offset += 4;
    input.set(field, offset);
    offset += field.byteLength;
  }

  const digest = await crypto.subtle.digest('SHA-256', input);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** The virtual URL path is stable for an immutable source within one page session. */
function mcapVirtualPath(sessionNonce: string, sourceId: string): string {
  return `${MCAP_VIRTUAL_PATH_PREFIX}/${encodeURIComponent(sessionNonce)}/${encodeURIComponent(sourceId)}`;
}

/**
 * Rosview workers require an absolute same-origin URL rather than a document
 * relative path, especially when their worker was created from a Blob URL.
 */
export function mcapVirtualUrl(sessionNonce: string, sourceId: string): string {
  return new URL(mcapVirtualPath(sessionNonce, sourceId), globalThis.location.origin).toString();
}

export function mcapVirtualGrantKey(sessionNonce: string, sourceId: string): string {
  return `${sessionNonce}\u0000${sourceId}`;
}
